import { Component, HostBinding, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { fuseAnimations } from '@fuse/animations';
import { LayoutConfigService } from 'fuse-core/services/layout-config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import {LayoutConfig} from 'fuse-core/types';

@Component({
    selector: 'fuse-theme-options',
    templateUrl: './theme-options.component.html',
    styleUrls: ['./theme-options.component.scss'],
    animations: fuseAnimations,
    standalone: false
})
export class FuseThemeOptionsComponent implements OnInit, OnDestroy
{
    layoutConfig: LayoutConfig;
    form: UntypedFormGroup;

    @HostBinding('class.bar-closed')
    barClosed: boolean;

    // Private
    private _unsubscribeAll: Subject<void>;

    /**
     * Constructor
     *
     * @param {FormBuilder} _formBuilder
     * @param {LayoutConfigService} _layoutConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {Renderer2} _renderer
     */
    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _layoutConfigService: LayoutConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _renderer: Renderer2
    )
    {
        // Set the defaults
        this.barClosed = true;

        // Set the private defaults
        this._unsubscribeAll = new Subject<void>();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Build the config form
        // noinspection TypeScriptValidateTypes
        this.form = this._formBuilder.group({
            layout          : this._formBuilder.group({
                style    : new UntypedFormControl(),
                width    : new UntypedFormControl(),
                navbar   : this._formBuilder.group({
                    background: new UntypedFormControl(),
                    folded    : new UntypedFormControl(),
                    hidden    : new UntypedFormControl(),
                    position  : new UntypedFormControl(),
                    variant   : new UntypedFormControl()
                }),
                toolbar  : this._formBuilder.group({
                    background: new UntypedFormControl(),
                    hidden    : new UntypedFormControl(),
                    position  : new UntypedFormControl()
                }),
                footer   : this._formBuilder.group({
                    background: new UntypedFormControl(),
                    hidden    : new UntypedFormControl(),
                    position  : new UntypedFormControl()
                }),
                sidepanel: this._formBuilder.group({
                    hidden: new UntypedFormControl(),
                    position  : new UntypedFormControl()
                })
            }),
            customScrollbars: new UntypedFormControl()
        });

        // Subscribe to the config changes
        this._layoutConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                // Update the stored config
                this.layoutConfig = config;

                // Set the config form values without emitting an event
                // so that we don't end up with an infinite loop
                this.form.setValue(config, {emitEvent: false});
            });

        // Subscribe to the specific form value changes (layout.style)
        this.form.get('layout.style').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {

                // Reset the form values based on the
                // selected layout style
                this._resetFormValues(value);

            });

        // Subscribe to the form value changes
        this.form.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                // Update the config
                this._layoutConfigService.config = config;
            });

        // Add customize nav item that opens the bar programmatically
        const customFunctionNavItem = {
            'id'      : 'custom-function',
            'title'   : 'Custom Function',
            'type'    : 'group',
            'icon'    : 'settings',
            'children': [
                {
                    'id'      : 'customize',
                    'title'   : 'Customize',
                    'type'    : 'item',
                    'icon'    : 'settings',
                    'function': () => {
                        this.toggleSidebarOpen('themeOptionsPanel');
                    }
                }
            ]
        };

        this._fuseNavigationService.addNavigationItem(customFunctionNavItem, 'end');
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        // Remove the custom function menu
        this._fuseNavigationService.removeNavigationItem('custom-function');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Reset the form values based on the
     * selected layout style
     *
     * @param value
     * @private
     */
    private _resetFormValues(value): void
    {
        switch ( value )
        {
            // Vertical Layout #1
            case 'vertical-layout-1':
            {
                this.form.patchValue({
                    layout: {
                        width  : 'fullwidth',
                        navbar : {
                            background: 'mat-fuse-dark-700-bg',
                            folded    : false,
                            hidden    : false,
                            position  : 'left',
                            variant   : 'vertical-style-1'
                        },
                        toolbar: {
                            background: 'mat-white-500-bg',
                            hidden    : false,
                            position  : 'below-static'
                        },
                        footer : {
                            background: 'mat-fuse-dark-900-bg',
                            hidden    : false,
                            position  : 'below-static'
                        }
                    }
                });

                break;
            }

            // Vertical Layout #2
            case 'vertical-layout-2':
            {
                this.form.patchValue({
                    layout: {
                        width  : 'fullwidth',
                        navbar : {
                            background: 'mat-fuse-dark-700-bg',
                            folded    : false,
                            hidden    : false,
                            position  : 'left',
                            variant   : 'vertical-style-1'
                        },
                        toolbar: {
                            background: 'mat-white-500-bg',
                            hidden    : false,
                            position  : 'below'
                        },
                        footer : {
                            background: 'mat-fuse-dark-900-bg',
                            hidden    : false,
                            position  : 'below'
                        }
                    }
                });

                break;
            }

            // Vertical Layout #3
            case 'vertical-layout-3':
            {
                this.form.patchValue({
                    layout: {
                        width  : 'fullwidth',
                        navbar : {
                            background: 'mat-fuse-dark-700-bg',
                            folded    : false,
                            hidden    : false,
                            position  : 'left',
                            layout    : 'vertical-style-1'
                        },
                        toolbar: {
                            background: 'mat-white-500-bg',
                            hidden    : false,
                            position  : 'above-static'
                        },
                        footer : {
                            background: 'mat-fuse-dark-900-bg',
                            hidden    : false,
                            position  : 'above-static'
                        }
                    }
                });

                break;
            }

            // Horizontal Layout #1
            case 'horizontal-layout-1':
            {
                this.form.patchValue({
                    layout: {
                        width  : 'fullwidth',
                        navbar : {
                            background: 'mat-fuse-dark-700-bg',
                            folded    : false,
                            hidden    : false,
                            position  : 'top',
                            variant   : 'vertical-style-1'
                        },
                        toolbar: {
                            background: 'mat-white-500-bg',
                            hidden    : false,
                            position  : 'above'
                        },
                        footer : {
                            background: 'mat-fuse-dark-900-bg',
                            hidden    : false,
                            position  : 'above-fixed'
                        }
                    }
                });

                break;
            }
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void
    {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

}
